/* product-grid-section.js - Isolated and conflict-free */

(function() {
  'use strict';
  
  // Namespace for this component to avoid conflicts
  const ProductGridComponent = {
    resizeTimeout: null,
    zoomTimeout: null,
    
    // Initialize when DOM is ready
    init: function() {
      this.initProductGrids();
    },
    
    // Main initialization function
    initProductGrids: function() {
      const productGridSections = document.querySelectorAll('.product-grid-section');
      
      productGridSections.forEach(section => {
        const grid = section.querySelector('.product-grid');
        if (grid) {
          // Setup scrolling for all devices
          this.makeScrollable(grid);
          
          // Setup desktop features only
          if (window.innerWidth >= 768) {
            this.setupDesktopNavigation(section, grid);
            this.setupClickZoom(grid);
            this.setupHoverEffects(grid);
          }
        }
      });
    },
    
    // Touch and mouse scrolling functionality
    makeScrollable: function(element) {
      let isDown = false;
      let startX;
      let scrollLeft;
      let hasMoved = false;
      
      // Mouse events
      element.addEventListener('mousedown', (e) => {
        if (e.target.closest('.grid-arrow')) return;
        
        isDown = true;
        startX = e.pageX - element.offsetLeft;
        scrollLeft = element.scrollLeft;
        hasMoved = false;
        element.style.cursor = 'grabbing';
      });
      
      element.addEventListener('mouseleave', () => {
        isDown = false;
        element.style.cursor = 'grab';
      });
      
      element.addEventListener('mouseup', () => {
        isDown = false;
        element.style.cursor = 'grab';
      });
      
      element.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        hasMoved = true;
        const x = e.pageX - element.offsetLeft;
        const walk = (x - startX) * 2;
        element.scrollLeft = scrollLeft - walk;
      });
      
      // Touch events
      let touchStartX = 0;
      let touchScrollLeft = 0;
      
      element.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].pageX;
        touchScrollLeft = element.scrollLeft;
        hasMoved = false;
      }, { passive: true });
      
      element.addEventListener('touchmove', (e) => {
        if (e.touches.length > 0) {
          hasMoved = true;
          const x = e.touches[0].pageX;
          const walk = (touchStartX - x) * 1.5;
          element.scrollLeft = touchScrollLeft + walk;
        }
      }, { passive: true });
      
      // Prevent click when dragging
      element.addEventListener('click', (e) => {
        if (hasMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    },
    
    // Desktop arrow navigation - properly isolated
    setupDesktopNavigation: function(section, grid) {
      const leftArrow = section.querySelector('.grid-arrow-left');
      const rightArrow = section.querySelector('.grid-arrow-right');
      
      if (!leftArrow || !rightArrow) return;
      
      // Calculate scroll amount
      const getScrollAmount = () => {
        const firstItem = grid.querySelector('.product-item');
        if (firstItem) {
          return firstItem.offsetWidth + 15;
        }
        return 200;
      };
      
      // Update arrow states
      const updateArrowStates = () => {
        const tolerance = 5;
        const isAtStart = grid.scrollLeft <= tolerance;
        const isAtEnd = grid.scrollLeft >= (grid.scrollWidth - grid.clientWidth - tolerance);
        const hasOverflow = grid.scrollWidth > grid.clientWidth;
        
        // Show/hide arrows based on overflow
        if (hasOverflow) {
          leftArrow.style.display = 'flex';
          rightArrow.style.display = 'flex';
        } else {
          leftArrow.style.display = 'none';
          rightArrow.style.display = 'none';
        }
        
        // Update states
        leftArrow.disabled = isAtStart;
        rightArrow.disabled = isAtEnd;
        
        leftArrow.style.opacity = isAtStart ? '0.4' : '1';
        rightArrow.style.opacity = isAtEnd ? '0.4' : '1';
      };
      
      // Arrow click handlers
      leftArrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!leftArrow.disabled) {
          const scrollAmount = getScrollAmount();
          grid.scrollBy({
            left: -scrollAmount,
            behavior: 'smooth'
          });
        }
      });
      
      rightArrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!rightArrow.disabled) {
          const scrollAmount = getScrollAmount();
          grid.scrollBy({
            left: scrollAmount,
            behavior: 'smooth'
          });
        }
      });
      
      // Listen for scroll events
      grid.addEventListener('scroll', updateArrowStates);
      
      // Initial update
      setTimeout(updateArrowStates, 100);
      
      // Update when images load
      const images = grid.querySelectorAll('img');
      images.forEach(img => {
        const updateOnLoad = () => setTimeout(updateArrowStates, 50);
        
        if (img.complete) {
          updateOnLoad();
        } else {
          img.addEventListener('load', updateOnLoad);
        }
      });
      
      // Keyboard navigation specific to this section
      section.addEventListener('keydown', (e) => {
        if (document.activeElement && section.contains(document.activeElement)) {
          if (e.key === 'ArrowLeft' && !leftArrow.disabled) {
            e.preventDefault();
            leftArrow.click();
          } else if (e.key === 'ArrowRight' && !rightArrow.disabled) {
            e.preventDefault();
            rightArrow.click();
          }
        }
      });
    },
    
    // Click zoom functionality (desktop only)
    setupClickZoom: function(grid) {
      const productItems = grid.querySelectorAll('.product-item');
      
      productItems.forEach(item => {
        const productLink = item.querySelector('.product-link');
        
        item.addEventListener('click', (e) => {
          if (e.defaultPrevented || e.target.closest('.grid-arrow')) return;
          
          e.preventDefault();
          
          if (this.zoomTimeout) {
            clearTimeout(this.zoomTimeout);
          }
          
          const isCurrentlyZoomed = item.classList.contains('zoomed');
          
          // Remove zoom from all items in this grid
          productItems.forEach(otherItem => {
            otherItem.classList.remove('zoomed', 'zoom-out');
          });
          
          if (!isCurrentlyZoomed) {
            item.classList.add('zoomed');
            
            // Auto-remove zoom after 2.5 seconds
            this.zoomTimeout = setTimeout(() => {
              item.classList.add('zoom-out');
              item.classList.remove('zoomed');
              
              setTimeout(() => {
                item.classList.remove('zoom-out');
              }, 300);
            }, 0);
            
            // Navigate after zoom
            if (productLink && productLink.href && productLink.href !== '#' && productLink.href !== window.location.href + '#') {
              setTimeout(() => {
                window.location.href = productLink.href;
              }, 400);
            }
          } else {
            if (productLink && productLink.href && productLink.href !== '#' && productLink.href !== window.location.href + '#') {
              window.location.href = productLink.href;
            }
          }
        });
      });
      
      // Close zoom when clicking outside this grid
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.product-grid-section')) {
          productItems.forEach(item => {
            item.classList.remove('zoomed', 'zoom-out');
          });
          if (this.zoomTimeout) {
            clearTimeout(this.zoomTimeout);
          }
        }
      });
    },
    
    // Hover effects for desktop only
    setupHoverEffects: function(grid) {
      const productItems = grid.querySelectorAll('.product-item');
      
      productItems.forEach(item => {
        const image = item.querySelector('.product-image');
        const title = item.querySelector('.product-title');
        
        item.addEventListener('mouseenter', () => {
          if (!item.classList.contains('zoomed')) {
            if (image) {
              image.style.transform = 'scale(1.05)';
            }
            if (title) {
              title.style.color = '#ff0000';
              title.style.transform = 'scale(1.05)';
            }
          }
        });
        
        item.addEventListener('mouseleave', () => {
          if (!item.classList.contains('zoomed')) {
            if (image) {
              image.style.transform = 'scale(1)';
            }
            if (title) {
              title.style.color = '#626262';
              title.style.transform = 'scale(1)';
            }
          }
        });
      });
    },
    
    // Handle window resize
    handleResize: function() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.initProductGrids();
      }, 250);
    }
  };
  
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      ProductGridComponent.init();
    });
  } else {
    ProductGridComponent.init();
  }
  
  // Handle window resize
  window.addEventListener('resize', function() {
    ProductGridComponent.handleResize();
  });
  
  // Handle orientation change
  window.addEventListener('orientationchange', function() {
    setTimeout(() => {
      ProductGridComponent.handleResize();
    }, 100);
  });
  
  // Error handling for images
  document.addEventListener('DOMContentLoaded', function() {
    const productGridSections = document.querySelectorAll('.product-grid-section');
    
    productGridSections.forEach(section => {
      const images = section.querySelectorAll('.product-image');
      images.forEach(img => {
        img.addEventListener('error', function() {
          this.style.display = 'none';
          const container = this.parentNode;
          let placeholder = container.querySelector('.product-image-placeholder');
          
          if (!placeholder) {
            placeholder = document.createElement('div');
            placeholder.className = 'product-image-placeholder';
            container.appendChild(placeholder);
          }
          placeholder.style.display = 'block';
        });
      });
    });
  });
  
  // Export for external use (avoid global conflicts)
  if (typeof window.ProductGridComponent === 'undefined') {
    window.ProductGridComponent = ProductGridComponent;
  }
  
})();